/**
 * Command palette live search (lane B6).
 *
 * Three small, brand-scoped list calls (top 5 each) against existing
 * endpoints — no palette-specific backend:
 *   GET /api/posts?q=&page_size=5[&client_brand_id=]           caption ILIKE (org-scoped)
 *   GET /api/designs?search=&limit=5[&client_brand_id=]        name OR tag ILIKE (org-scoped)
 *   GET /api/content-ideas?q=&limit=5[&client_brand_id=]       title/hook/format ILIKE
 *
 * Each query only runs while the palette is open and the (debounced) term is
 * long enough, and each section reports its own loading / error state.
 *
 * @module api/commandPalette
 */

import { useApiQuery } from "@plerity/dashboard-shared/hooks";
import type { DesignListItem } from "../types/design";
import type { Post } from "../types/social";
import { useContentIdeas } from "./contentIdeas";
import { socialKeys } from "./keys";
import { objectResponseGuard } from "./schemas";

const PALETTE_LIMIT = 5;

export function usePaletteSearch(term: string, brandId: string | null, enabled: boolean) {
  const q = term.trim();
  const brand = brandId ? { client_brand_id: brandId } : {};

  const postParams = { q, page_size: PALETTE_LIMIT, ...brand };
  const posts = useApiQuery<{ items: Post[]; total?: number }>(
    socialKeys.list("palette-posts", postParams),
    "/api/posts",
    { enabled, axiosConfig: { params: postParams }, schema: objectResponseGuard<{ items: Post[]; total?: number }>() }
  );

  const designParams = { search: q, limit: PALETTE_LIMIT, ...brand };
  const designs = useApiQuery<{ items: DesignListItem[]; total: number }>(
    socialKeys.list("palette-designs", designParams),
    "/api/designs",
    {
      enabled,
      axiosConfig: { params: designParams },
      schema: objectResponseGuard<{ items: DesignListItem[]; total: number }>(),
    }
  );

  const ideas = useContentIdeas(brandId, { q, limit: PALETTE_LIMIT }, { enabled });

  return { posts, designs, ideas };
}
